import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Printer, AlertTriangle } from 'lucide-react';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import ProgressReport from '../features/learnerPlan/report/ProgressReport';
import type { IssuedStamp } from '../features/learnerPlan/report/ProgressReport';
import {
  getMyProgressReport, getStudentProgressReport, getMyIssuedReport, getMyIssuedReportById,
  type ProgressReportDto,
} from '../features/learnerPlan/learnerPlanApi';

// /reports/learner-plan/:userId — the printable progress report, deliberately
// mounted OUTSIDE the AppShell (same reasoning as /project/:sessionId in
// routes.tsx): there is no nav, no tab bar and no top bar on the page, so
// Print to PDF produces the document and nothing else.
//
// Hiding the shell at print time with `visibility: hidden` on everything else
// was the alternative, and it is the fragile one — it breaks the moment any
// ancestor of the report has `overflow: hidden`, which is exactly the kind of
// thing a layout change adds without anyone noticing until a printout comes
// out cropped. A page with no chrome on it cannot be cropped by chrome.
//
// `:userId` may be the literal `me`, which is how a student reaches their own
// report; the backend never takes a student's word for whose report to build
// (GET /report/me reads req.user.id).
//
// `?issued=latest` (or `?issued=<id>`) opens an ISSUED SNAPSHOT instead of the
// live view — the copy an instructor fixed in place and put on the student's
// dashboard. This is the route the dashboard link uses, and it is why the
// student's entry point does not need a second print path: it is the same
// chrome-free page over the same stylesheet, rendering a stored payload rather
// than a freshly built one.
//
// A snapshot is only ever fetched through the caller's OWN endpoints
// (`/report/issued/me...`), which take no user id — so this page cannot be
// pointed at someone else's issued report even by hand-editing the URL.
export default function ProgressReportPage() {
  const { userId } = useParams<{ userId: string }>();
  const [params] = useSearchParams();
  const issuedParam = params.get('issued');
  const [report, setReport] = useState<ProgressReportDto | null>(null);
  const [issued, setIssued] = useState<IssuedStamp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setIssued(null);
      try {
        if (issuedParam) {
          const record = issuedParam === 'latest'
            ? await getMyIssuedReport()
            : await getMyIssuedReportById(issuedParam);
          if (cancelled) return;
          if (!record) {
            setError('No progress report has been issued to you yet.');
            return;
          }
          setReport(record.report);
          setIssued({ issued_at: record.issued_at });
          return;
        }
        const data = !userId || userId === 'me'
          ? await getMyProgressReport()
          : await getStudentProgressReport(userId);
        if (!cancelled) setReport(data);
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (!cancelled) {
          setError(status === 403 || status === 404
            ? 'You do not have access to this report.'
            : 'This report could not be loaded.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, issuedParam]);

  useEffect(() => {
    if (report) document.title = `Progress report — ${report.student.name}`;
  }, [report]);

  return (
    <div className="min-h-screen bg-ws-bg px-4 py-6 print:bg-white print:p-0">
      <div className="lpr-no-print mx-auto mb-4 flex max-w-[60rem] items-center justify-between gap-3">
        <p className="text-[12.5px] text-ws-ink-3">
          Print, or choose "Save as PDF" as the destination in the print dialog.
        </p>
        <Button onClick={() => window.print()} disabled={!report}>
          <Printer size={15} /> Print / Save as PDF
        </Button>
      </div>

      {loading && <div className="mx-auto max-w-[60rem]"><Spinner label="Building the report…" /></div>}

      {error && (
        <p className="mx-auto flex max-w-[60rem] items-center gap-1.5 text-[13px] text-ws-danger">
          <AlertTriangle size={14} /> {error}
        </p>
      )}

      {report && <ProgressReport report={report} issued={issued} />}
    </div>
  );
}
